import { Search } from './search-services';
import panelUpdater from '@remarkablenet/panel-updater';
import * as CSSTH from '../css-transition-helper/css-transition-helper';
import {
  debounce,
  disableScroll,
  dispatchCustomEventWrapper,
  enableScroll
} from '../utility-functions/utility-functions';
import { closeOffCanvasPanel, createBackdrop, handleCloseEvent } from '../off-canvas-panel/off-canvas-panel';
import { dispatchEventWrapper } from '../event/event';
import * as slideoutMenu from '../slideout-menu/slideout-menu';
import { isMobile, isTouch, addDelegateEventListener } from '@remarkablenet/rcm-utils';
import { showSolidHeader, showGlassHeader } from '../../global';

export const DEFAULTS = {
  toggleSearch: '.js-toggle-search',
  searchContainer: '.search-container.off-canvas-panel',
  createBackDrop: true,
  backdropType: 'real', // 'fake' or 'real'
  searchInput: '.js-search-input',
  editorialCountInput: '.js-editorial-count-input',
  searchInputClear: '.js-search-input-clear',
  searchResults: '.js-search-results',
  searchContainerClose: '.js-search-container-close',
  searchForm: '.search-form',
  suggestedSearch: '.js-suggested-search'
};

const SEARCH_RESULTS_SELECTOR = `${DEFAULTS.searchContainer} ${DEFAULTS.searchResults}`;

// Global click listener function that can be added/removed
let globalClickHandler: ((e: Event) => void) | null = null;
let searchCloseHandler: ((e: Event) => void) | null = null;

export const init = () => {
  addSearchContainerEventListeners();
  addSearchFormListeners(document.querySelectorAll(DEFAULTS.searchForm));
};

/**
 * External function to close search panel from other modules
 * Can be called by basket, wishlist, or any other external component
 */
export const closeSearchFromExternal = () => {
  const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;
  if (searchContainer && searchContainer.classList.contains('is-active')) {
    searchCloseEvents();
  }
};

export const updateUI = (resultElm: HTMLElement, content) => (resultElm.innerHTML = content);

export const clearSearchInput = (searchContainer: HTMLElement) => {
  const searchInput = searchContainer.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
  searchInput.value = '';
};
export const getDefaultState = () => {
  const resultElmList: NodeListOf<HTMLElement> = document.querySelectorAll(SEARCH_RESULTS_SELECTOR);
  Search('').then((response) => {
    resultElmList.forEach((resultElm) => updateUI(resultElm, response));
  })
};

export const openSearchPanel = (target: HTMLElement) => {
  // Close any open slideout menu first so scroll lock is cleanly
  // re-applied for the search panel only.
  checkToCloseMenu();
  disableScroll();
  document.documentElement.classList.add('search-open');
  if (!isTouch()) showSolidHeader();

  // Add both global click listener and search close event listener when search opens
  addGlobalClickListener();
  addSearchCloseEventListener();

  if (DEFAULTS.createBackDrop) {
    if (DEFAULTS.backdropType === 'fake') document.body.classList.add('search-fake-backdrop');
    createBackdrop().then((backdrop) => {
      if (!isMobile()) CSSTH.activate(backdrop);

      backdrop.addEventListener('click', () => {
        handleCloseEvent(false);
        searchCloseEvents();
      });
    });
  }
  return CSSTH.activate(target);
};

const closeSearchPanel = async (target: HTMLElement) => {
  // Wait before clearing the input to allow the transition to finish
  // and prevent the input from being cleared before the transition ends.
  // To stop jankiness of everything looking like it is staggered.
  await closeOffCanvasPanelAwaitable(target);
  enableScroll();
};

/* This function is a wrapper around the closeOffCanvasPanel function to
  ensure that the transitionend event is handled correctly.
  It waits for the transition to finish before resolving the promise.
*/
const closeOffCanvasPanelAwaitable = (target: HTMLElement): Promise<void> => {
  return new Promise((resolve) => {
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === target) {
        target.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      }
    };

    // Add the listener *before* starting the transition
    target.addEventListener('transitionend', onTransitionEnd);

    // original function call
    closeOffCanvasPanel(target);

    setTimeout(() => {
      target.removeEventListener('transitionend', onTransitionEnd);
      resolve();
    }, 500);
  });
};

const performSearch: Function = (QUERY: string, searchForm: HTMLFormElement) => {
  const resultsPanel = searchForm
    .closest(DEFAULTS.searchContainer)
    .querySelector(DEFAULTS.searchResults) as HTMLElement;
  panelUpdater(resultsPanel, Search(QUERY)).then((response) => {
    updateUI(resultsPanel, response);
    dispatchEventWrapper(document, 'rc-quick-search', {
      area: 'Search',
      event: 'Quick Search',
      value: QUERY
    });
  }).then(() => {
    let editorialCount = 0;
    const editorialCountElement = document.querySelector<HTMLElement>('.js-search-form-editorial-query');
    if (editorialCountElement) {
      editorialCount = parseInt(editorialCountElement.getAttribute("data-editorial-count"), 10);
    }

    const editorialCountInput = document.querySelector<HTMLInputElement>(DEFAULTS.editorialCountInput);

    if (editorialCountInput) {
      editorialCountInput.value = editorialCount > 0 ? editorialCount.toString() : '0';
    }
  });
};

const suggestedSearch = (suggestedSearchItem: HTMLElement, form: HTMLFormElement) => {
  const searchInput: HTMLInputElement = document.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
  const suggestedTerm: string = suggestedSearchItem.getAttribute('data-searchterm');
  searchInput.value = suggestedTerm;
  performSearch(suggestedTerm, form);
};

const addSearchContainerEventListeners = () => {
  const triggerElements = document.querySelectorAll(DEFAULTS.toggleSearch);
  const closeTriggers = document.querySelectorAll(DEFAULTS.searchContainerClose);

  triggerElements.forEach((triggerElement) => {
    triggerElement.addEventListener('click', (e) => {
      e.preventDefault();
      const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;
      openSearchPanel(searchContainer).then(() => {
        const searchInput = searchContainer.querySelector(DEFAULTS.searchInput) as HTMLInputElement;
        searchInput.focus();
        //iOSClickFocus(searchInput);
      });
    });
  });
  closeTriggers.forEach((closeTrigger) => {
    closeTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      searchCloseEvents();
    });
  });
};

// function iOSClickFocus(targetInput) {
//   // create invisible dummy input to receive the focus first
//   const fakeInput = document.createElement('input');
//   fakeInput.setAttribute('type', 'text');
//   fakeInput.setAttribute('autofocus', 'true');
//   fakeInput.style.position = 'absolute';
//   fakeInput.style.opacity = `0`;
//   fakeInput.style.height = `0`;
//   fakeInput.style.fontSize = '16px'; // disable auto zoom
//   // you may need to append to another element depending on the browser's auto
//   // zoom/scroll behavior
//   document.body.prepend(fakeInput);
//   // focus so that subsequent async focus will work
//   fakeInput.focus();
//   setTimeout(() => {
//     // now we can focus on the target input
//     targetInput.focus();
//     // cleanup
//     fakeInput.remove();
//   }, 1000);
// }

const searchCloseEvents = async () => {
  const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;
  await closeSearchPanel(searchContainer);

  // Remove both global click listener and search close event listener when search closes
  removeGlobalClickListener();
  removeSearchCloseEventListener();

  clearSearchInput(searchContainer);
  getDefaultState();
  document.documentElement.classList.remove('search-open');
  // Now that the search-open flag is gone, safely allow the header
  // to return to its glass state (if at the top and no other panels are open).
  showGlassHeader();
};

const addSearchCloseEventListener = () => {
  // Remove existing listener if it exists
  removeSearchCloseEventListener();

  searchCloseHandler = (e: Event) => {
    searchCloseEvents();
  };

  document.addEventListener('rc-close-search', searchCloseHandler);
};

const removeSearchCloseEventListener = () => {
  if (searchCloseHandler) {
    document.removeEventListener('rc-close-search', searchCloseHandler);
    searchCloseHandler = null;
  }
};

const addGlobalClickListener = () => {
  removeGlobalClickListener();

  globalClickHandler = (e: Event) => {
    const searchContainer = document.querySelector(DEFAULTS.searchContainer) as HTMLElement;

    // Only proceed if search is currently open
    if (!searchContainer || !searchContainer.classList.contains('is-active')) {
      return;
    }

    const target = e.target as HTMLElement;

    // Check if click is inside search container or on search toggle button
    const isInsideSearch = searchContainer.contains(target);
    const isSearchToggle = target.closest(DEFAULTS.toggleSearch);

    // If click is outside search and not on toggle button, close search
    if (!isInsideSearch && !isSearchToggle) {
      dispatchCustomEventWrapper(document, 'rc-close-search', {});
    }
  };

  document.addEventListener('click', globalClickHandler);
};

const removeGlobalClickListener = () => {
  if (globalClickHandler) {
    document.removeEventListener('click', globalClickHandler);
    globalClickHandler = null;
  }
};

const addSearchFormListeners = (searchForms: NodeListOf<HTMLFormElement>) => {
    searchForms.forEach((searchForm) => {
        const searchInput = searchForm.querySelector(DEFAULTS.searchInput) as HTMLInputElement;

        searchInput.addEventListener(
            'keyup',
            debounce((e) => {
                e.preventDefault();
                const QUERY = searchInput.value;
                const encodedQuery = encodeURIComponent(QUERY);

                sessionStorage.setItem('bloomreach_searchterm', QUERY);

                performSearch(encodedQuery, searchForm);
            }, 350)
        );

        searchForm.addEventListener('submit', (e) => {
            const q = searchInput?.value.trim().toLowerCase();
            searchInput.value = q;
            if (q) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'search_form_submit',
                    search_term: q
                });
            }
        });

        const searchInputClear = searchForm.querySelector(DEFAULTS.searchInputClear) as HTMLButtonElement;
        searchForm.addEventListener('reset', (e) => {
            e.preventDefault();
            getDefaultState();
            clearSearchInput(searchForm.closest(DEFAULTS.searchContainer) as HTMLElement);
        });

        addDelegateEventListener({ container: searchForm, event: 'click', target: DEFAULTS.suggestedSearch, callback: (e) => {
            e.originalEvent.preventDefault();
            suggestedSearch(<HTMLElement>e.originalEvent.target, searchForm);
        } });
    });
};


const checkToCloseMenu = () => {
  if (!document.querySelector('html').classList.contains('slideout-open')) return;
  dispatchCustomEventWrapper(document, 'rc-close-menu-fn', {});
};
